import { createSelector } from '@reduxjs/toolkit';
import { isEmpty, filter as lodashFilter, map } from 'lodash';
import { ShipmentStatus } from 'enums/common.enum';
import {
  getCreatedAfterForCurrentWindow,
  getStartOfToday,
  getStartOfYesterday,
} from '../utils/shipmentUtils';

/** Shared so a missing tenant or allow-list returns the same reference every time. */
const NO_FACILITIES = [];

const compareByName = (a, b) =>
  String(a?.name ?? '').localeCompare(String(b?.name ?? ''), undefined, { sensitivity: 'base' });

/**
 * Returns facilities the current user has access to, A–Z by name (the pharmacy store holds them
 * in API order, and every facility tab and sidebar list reads this).
 * When auth.fulfillingPharmacies is non-empty (user has restricted access), filters
 * pharmacy list to only those IDs. When empty (e.g. admin), returns all tenant facilities.
 *
 * Memoised: sorting hands back a new array, and the shipment screens read this through
 * useSelector, so an unmemoised sort would re-render them on every unrelated dispatch.
 */
export const selectAccessibleFacilities = createSelector(
  [
    (state) => state.pharmacy?.[state.auth?.currentTenantId] ?? NO_FACILITIES,
    (state) => state.auth?.fulfillingPharmacies ?? NO_FACILITIES,
  ],
  (allFacilities, userFulfillingPharmacies) => {
    if (isEmpty(userFulfillingPharmacies)) {
      return [...allFacilities].sort(compareByName);
    }
    const allowedIds = new Set(map(userFulfillingPharmacies, 'fulfillingPharmacyId'));
    return lodashFilter(allFacilities, (f) => allowedIds.has(f?.fulfillingPharmacyId)).sort(
      compareByName
    );
  }
);

/**
 * Computes the effective filter for the shipment list API call
 * based on the current category, subcategory, preset, and pharmacy selections.
 */
export const selectEffectiveShipmentFilter = (state) => {
  const { activeCategory, activeSubcategory, activeFacility, activePreset, list } =
    state.shipmentModule;

  const filter = { ...list.filter };

  // Category -> status (using PRX status values directly)
  if (activeCategory === 'awaitingShipment') {
    filter.shipmentStatus = [ShipmentStatus.REQUEST_CREATED];
  } else if (activeCategory === 'shipped') {
    // For "voided" view we intentionally rely ONLY on isVoided flag (no shipmentStatus filter)
    if (activeSubcategory !== 'voided') {
      filter.shipmentStatus = [ShipmentStatus.REQUEST_SHIPPED];
    }
  }

  // Subcategory -> pharmacy or special filters
  if (activeCategory === 'awaitingShipment') {
    // activeSubcategory is a pharmacyId in the sidebar
    if (activeSubcategory !== 'all') {
      filter.fulfillingPharmacyIds = [activeSubcategory];
    }
    // Preset -> additional filters (additive, only for awaiting)
    if (activePreset === 'current') {
      filter.createdAfter = getCreatedAfterForCurrentWindow();
    } else if (activePreset === 'badAddresses') {
      filter.addressValidity = ['WARN', 'INVALID', 'BLOCKED'];
    } else if (activePreset === 'manual') {
      filter.isManualShipment = true;
    }
  } else if (activeCategory === 'shipped') {
    if (activeSubcategory === 'voided') {
      filter.isVoided = true;
    } else {
      // "All", "Today", "Yesterday", and "Recent" shipped views exclude voided shipments
      filter.isVoided = false;
      // Only apply subcategory preset date range when the user hasn't set an explicit shipped date filter
      const hasCustomShippedDateFilter =
        list.filter?.shippedDateAfter || list.filter?.shippedDateBefore;
      if (!hasCustomShippedDateFilter) {
        if (activeSubcategory === 'today') {
          filter.shippedDateAfter = getStartOfToday();
        } else if (activeSubcategory === 'yesterday') {
          filter.shippedDateAfter = getStartOfYesterday();
          filter.shippedDateBefore = getStartOfToday();
        } else if (activeSubcategory === 'recent') {
          filter.shippedDateAfter = getCreatedAfterForCurrentWindow();
        }
      }
    }
    // Facility tab -> pharmacy filter (shipped only, in content area)
    if (activeFacility !== 'all') {
      filter.fulfillingPharmacyIds = [activeFacility];
    }
  }

  return filter;
};

export const selectCurrentPageItems = (state) => {
  const { currentPage, pages } = state.shipmentModule.list;
  return pages[currentPage] || [];
};

export const selectShipmentModuleState = (state) => state.shipmentModule;
